import { useEffect, useRef } from "react";

/**
 * Auto-refresh hook.
 * Calls `callback` at three moments:
 *  1) On mount (already covered by the page's own useEffect, but included for safety)
 *  2) Every `intervalMs` while the tab is visible (default 20s)
 *  3) When the tab regains focus (user comes back from another tab / another window)
 *
 * Pauses when the tab is hidden, resumes when it becomes visible again.
 * Useful for multi-user real-time-ish sync without websockets.
 *
 * Usage:
 *   const load = useCallback(async () => { ... }, [site]);
 *   useAutoRefresh(load);
 */
export function useAutoRefresh(callback, intervalMs = 20000) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!callbackRef.current) return;
    let cancelled = false;
    let timer = null;

    const tick = () => {
      if (cancelled) return;
      if (document.visibilityState === "visible") {
        callbackRef.current?.();
      }
    };

    const start = () => {
      if (timer) return;
      timer = setInterval(tick, intervalMs);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Immediate refresh when the user comes back to the tab
        callbackRef.current?.();
        start();
      } else {
        stop();
      }
    };
    const onFocus = () => {
      callbackRef.current?.();
    };

    // Start immediately if already visible
    if (document.visibilityState === "visible") start();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [intervalMs]);
}

export default useAutoRefresh;
